import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { responseHandler, Loading } from './utility/Utility';
import ConfirmDialog from './ConfirmDialog';
import ellipsisIcon from '../assets/images/icons/ellipsis-vertical-solid.svg';

function formatPermission(role) {
  if (role === 'owner') return 'Owner';
  if (role === 'viewer') return 'Viewer';
  return 'Admin';
}

function formatStatus(row) {
  if (row.isOwner || row.status === 'active') return 'Active';
  return 'Pending';
}

/** Members first (owner, then active), pending invites last; dedupe StoreInvite rows already on memberships. */
function buildTeamRows(members, pendingInvites) {
  const emailsOnMembers = new Set(
    members.map((m) => m.email?.toLowerCase()).filter(Boolean)
  );

  const rows = members.map((m) => ({
    key: String(m._id),
    membershipId: m._id,
    email: m.email,
    role: m.role,
    status: m.status,
    isOwner: m.role === 'owner',
    isPending: m.status === 'invited'
  }));

  for (const inv of pendingInvites) {
    const email = inv.email?.toLowerCase();
    if (email && !emailsOnMembers.has(email)) {
      rows.push({
        key: `invite-${inv._id}`,
        membershipId: null,
        inviteId: inv._id,
        email: inv.email,
        role: inv.role,
        status: 'invited',
        isOwner: false,
        isPending: true
      });
    }
  }

  const rank = (r) => {
    if (r.isOwner) return 0;
    if (!r.isPending) return 1;
    return 2;
  };

  rows.sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    return (a.email || '').localeCompare(b.email || '');
  });

  return rows;
}

function TeamRowMenu({ row, openMenuUpward, onDeleteUser, onCancelInvite }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  if (row.isOwner) {
    return <div className="settings__team-actions" />;
  }

  const handleAction = () => {
    if (row.isPending) {
      onCancelInvite(row);
    } else {
      onDeleteUser(row);
    }
    setOpen(false);
  };

  return (
    <div className="settings__team-actions" ref={rootRef}>
      <button
        type="button"
        className="settings__team-menu-btn"
        aria-label="Row actions"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <img src={ellipsisIcon} alt="" className="settings__team-menu-icon" />
      </button>
      {open && (
        <div
          className={`settings__team-menu${openMenuUpward ? ' settings__team-menu--above' : ''}`}
          role="menu"
        >
          <button type="button" className="settings__team-menu-item" role="menuitem" onClick={handleAction}>
            {row.isPending ? 'Cancel invite' : 'Delete user'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function SettingsTeam() {
  const { getJwtToken, isOwner } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [submitting, setSubmitting] = useState(false);
  const [cancelInviteTarget, setCancelInviteTarget] = useState(null);
  const [cancellingInvite, setCancellingInvite] = useState(false);
  const [resendInviteTarget, setResendInviteTarget] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(false);
  const [roleDrafts, setRoleDrafts] = useState({});
  const [savingRoles, setSavingRoles] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  const teamRows = useMemo(
    () => buildTeamRows(members, pendingInvites),
    [members, pendingInvites]
  );

  const getRowRole = (row) => {
    if (row.isOwner || row.isPending) return row.role;
    const draft = roleDrafts[row.membershipId];
    return draft !== undefined ? draft : row.role;
  };

  const isRowRoleDirty = (row) =>
    !row.isOwner && !row.isPending && getRowRole(row) !== row.role;

  const hasPendingRoleChanges = teamRows.some(isRowRoleDirty);

  const loadTeam = async () => {
    setLoading(true);
    try {
      const token = await getJwtToken();
      const res = await axios.get('https://api.amplifyloyalty.com/app/team', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(res.data.members || []);
      setPendingInvites(res.data.pendingInvites || []);
      setRoleDrafts({});
    } catch (err) {
      responseHandler(err, 'error', 'Could not load team.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) loadTeam();
  }, [isOwner]);

  if (!isOwner) {
    return (
      <p className="settings__sub-heading">
        Only the store owner can manage team members.
      </p>
    );
  }

  const postInvite = async ({ inviteEmail, inviteRole, resend = false }) => {
    const token = await getJwtToken();
    return axios.post(
      'https://api.amplifyloyalty.com/app/team/invite',
      { email: inviteEmail.trim().toLowerCase(), role: inviteRole, resend },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await postInvite({ inviteEmail: email, inviteRole: role, resend: false });
      setEmail('');
      const message = response.data?.resent ? 'Invitation resent.' : 'Invitation sent.';
      responseHandler({ status: 200 }, 'success', message);
      await loadTeam();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'PENDING_INVITE') {
        setResendInviteTarget({ email: email.trim().toLowerCase(), role });
        return;
      }
      if (code === 'ACTIVE_MEMBER') {
        responseHandler(err, 'error', 'This email is already an active team member.');
        return;
      }
      responseHandler(err, 'error', err.response?.data?.message || 'Could not send invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeResendInviteDialog = () => {
    if (!resendingInvite) setResendInviteTarget(null);
  };

  const confirmResendInvite = async () => {
    if (!resendInviteTarget) return;
    setResendingInvite(true);
    try {
      const response = await postInvite({
        inviteEmail: resendInviteTarget.email,
        inviteRole: resendInviteTarget.role,
        resend: true
      });
      setEmail('');
      setResendInviteTarget(null);
      responseHandler({ status: 200 }, 'success', response.data?.resent ? 'Invitation resent.' : 'Invitation sent.');
      await loadTeam();
    } catch (err) {
      responseHandler(err, 'error', err.response?.data?.message || 'Could not resend invite.');
    } finally {
      setResendingInvite(false);
    }
  };

  const handleRoleDraftChange = (membershipId, newRole) => {
    setRoleDrafts((prev) => ({ ...prev, [membershipId]: newRole }));
  };

  const resetRoleChanges = () => {
    setRoleDrafts({});
  };

  const saveRoleChanges = async () => {
    const dirtyRows = teamRows.filter(isRowRoleDirty);
    if (dirtyRows.length === 0) return;

    setSavingRoles(true);
    try {
      const token = await getJwtToken();
      await Promise.all(
        dirtyRows.map((row) =>
          axios.patch(
            `https://api.amplifyloyalty.com/app/team/${row.membershipId}`,
            { role: getRowRole(row) },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      setRoleDrafts({});
      responseHandler({ status: 200 }, 'success', 'Permissions updated.');
      await loadTeam();
    } catch (err) {
      responseHandler(err, 'error', err.response?.data?.message || 'Could not update permissions.');
    } finally {
      setSavingRoles(false);
    }
  };

  const revokeMembership = async (membershipId) => {
    const token = await getJwtToken();
    await axios.delete(`https://api.amplifyloyalty.com/app/team/${membershipId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  const requestDeleteUser = (row) => {
    setDeleteUserTarget(row);
  };

  const closeDeleteUserDialog = () => {
    if (!deletingUser) setDeleteUserTarget(null);
  };

  const confirmDeleteUser = async () => {
    if (!deleteUserTarget) return;
    setDeletingUser(true);
    try {
      await revokeMembership(deleteUserTarget.membershipId);
      setRoleDrafts((prev) => {
        const next = { ...prev };
        delete next[deleteUserTarget.membershipId];
        return next;
      });
      setDeleteUserTarget(null);
      responseHandler({ status: 200 }, 'success', 'User removed.');
      await loadTeam();
    } catch (err) {
      responseHandler(err, 'error', err.response?.data?.message || 'Could not remove user.');
    } finally {
      setDeletingUser(false);
    }
  };

  const performCancelInvite = async (row) => {
    if (!row.membershipId) {
      responseHandler(
        { status: 400 },
        'error',
        'Could not cancel invite. Refresh the page and try again.'
      );
      return;
    }
    await revokeMembership(row.membershipId);
    responseHandler({ status: 200 }, 'success', 'Invitation cancelled.');
    await loadTeam();
  };

  const cancelInvite = (row) => {
    setCancelInviteTarget(row);
  };

  const closeCancelInviteDialog = () => {
    if (!cancellingInvite) setCancelInviteTarget(null);
  };

  const confirmCancelInvite = async () => {
    if (!cancelInviteTarget) return;
    setCancellingInvite(true);
    try {
      await performCancelInvite(cancelInviteTarget);
      setCancelInviteTarget(null);
    } catch (err) {
      responseHandler(err, 'error', err.response?.data?.message || 'Could not cancel invite.');
    } finally {
      setCancellingInvite(false);
    }
  };

  return (
    <div className="settings__team">
      <h3 className="settings__heading">Team</h3>
      <p className="settings__sub-heading">
        Invite teammates as Admin (full access except billing and team) or Viewer (read-only).
      </p>

      <form onSubmit={sendInvite} className="settings__team-invite">
        <input
          type="email"
          placeholder="colleague@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={submitting || loading}
          className="settings__option-input"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={submitting || loading}
          className="settings__option-input settings__option-input--select"
        >
          <option value="admin">Admin</option>
          <option value="viewer">Viewer</option>
        </select>
        <button type="submit" className="gradient btn" disabled={submitting || loading}>
          Send invite
        </button>
      </form>

      <h4 className="settings__team-section-title">Members</h4>

      <div className="settings__team-table">
        <div className="settings__team-table-header">
          <span>Email</span>
          <span>Permissions</span>
          <span>Status</span>
          <span className="settings__team-table-header-actions" aria-hidden="true" />
        </div>

        {loading ? (
          <div className="settings__team-table-loading">
            <Loading className="settings__team-loading" />
          </div>
        ) : teamRows.length === 0 ? (
          <div className="settings__team-table-empty">No team members yet.</div>
        ) : (
          teamRows.map((row, index) => (
            <div key={row.key} className="settings__team-table-row">
              <span className="settings__team-cell settings__team-cell--email">{row.email}</span>

              <span className="settings__team-cell settings__team-cell--permission">
                {row.isOwner ? (
                  formatPermission(row.role)
                ) : row.isPending ? (
                  formatPermission(row.role)
                ) : (
                  <select
                    value={getRowRole(row)}
                    onChange={(e) => handleRoleDraftChange(row.membershipId, e.target.value)}
                    disabled={savingRoles}
                    className={`settings__option-input settings__option-input--select settings__team-role-select${
                      isRowRoleDirty(row) ? ' settings__team-role-select--dirty' : ''
                    }`}
                    aria-label={`Permissions for ${row.email}`}
                  >
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                )}
              </span>

              <span className="settings__team-cell settings__team-cell--status">
                {formatStatus(row)}
              </span>

              <TeamRowMenu
                row={row}
                openMenuUpward={index >= teamRows.length - 2}
                onDeleteUser={requestDeleteUser}
                onCancelInvite={cancelInvite}
              />
            </div>
          ))
        )}
      </div>

      {hasPendingRoleChanges && (
        <div className="settings__team-role-bar" role="status">
          <span className="settings__team-role-bar-text">You have unsaved permission changes.</span>
          <div className="settings__team-role-bar-actions">
            <button
              type="button"
              className="btn settings__team-role-bar-btn"
              onClick={resetRoleChanges}
              disabled={savingRoles}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn gradient settings__team-role-bar-btn"
              onClick={saveRoleChanges}
              disabled={savingRoles}
            >
              {savingRoles ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteUserTarget)}
        title="Remove team member?"
        message={
          deleteUserTarget
            ? `Remove ${deleteUserTarget.email} from this store? They will lose access immediately.`
            : ''
        }
        confirmLabel="Remove user"
        cancelLabel="Cancel"
        confirming={deletingUser}
        onConfirm={confirmDeleteUser}
        onCancel={closeDeleteUserDialog}
      />

      <ConfirmDialog
        open={Boolean(cancelInviteTarget)}
        title="Cancel invitation?"
        message={
          cancelInviteTarget
            ? `Remove the pending invite for ${cancelInviteTarget.email}? They will no longer be able to join with that invitation link.`
            : ''
        }
        confirmLabel="Cancel invite"
        cancelLabel="Keep invite"
        confirming={cancellingInvite}
        onConfirm={confirmCancelInvite}
        onCancel={closeCancelInviteDialog}
      />

      <ConfirmDialog
        open={Boolean(resendInviteTarget)}
        title="Resend invitation?"
        message={
          resendInviteTarget
            ? `A pending invite for ${resendInviteTarget.email} already exists. Send a fresh invitation email?`
            : ''
        }
        confirmLabel="Resend invite"
        cancelLabel="Not now"
        confirming={resendingInvite}
        onConfirm={confirmResendInvite}
        onCancel={closeResendInviteDialog}
      />
    </div>
  );
}
